// ═══════════════════════════════════════════════════════════════════════════
// DISTIL PROTOCOL - SAFE TOKENS MANAGER v3
// Three-tier protection: verified addresses → partner tokens → symbol fallback
//
// SECURITY MODEL:
//   Tier 1 — Verified coin_type address match (Suiet API) → always protected
//   Tier 2 — Partner tokens (hardcoded full coin type) → always protected
//   Tier 3 — Symbol fallback → only protected if USD value > threshold
//             This prevents fake tokens with matching symbols from hiding
//             in Protected Assets instead of being cleaned up.
// ═══════════════════════════════════════════════════════════════════════════

const TOKEN_LIST_URLS = [
  'https://raw.githubusercontent.com/suiet/sui-coin-list/main/src/coins.json',
  'https://raw.githubusercontent.com/suiet/coin-list/main/src/coins.json',
  'https://raw.githubusercontent.com/MystenLabs/sui-coin-list/main/coins.json',
];

const CACHE_DURATION = 86400000; // 24 hours

// ═══════════════════════════════════════════════════════════════════════════
// TIER 2 — PARTNER TOKENS (always protected, full coin type)
// These get VIP treatment regardless of value or address verification.
// When TOILET partnership is confirmed, replace the placeholder with
// the real coin type from the Admin Panel → Config tab.
// ═══════════════════════════════════════════════════════════════════════════

export const PARTNER_TOKEN_TYPES = [
  // $TOILET — Official Distil Protocol partner 👑🚽
  // TODO (C1): Replace placeholder with real coin type when partnership confirmed
  '0x...::toilet::TOILET',
];

// Partner token symbols — used as fallback until real addresses are confirmed
export const PARTNER_TOKEN_SYMBOLS = [
  'TOILET',
];

// ═══════════════════════════════════════════════════════════════════════════
// TIER 3 — MANUAL SYMBOL LIST (fallback only, requires USD value gate)
// Tokens matched only by symbol are NOT auto-protected.
// They are protected only if they also have USD value > MIN_SYMBOL_MATCH_USD.
// This means a fake "AAA" with zero/dust value gets cleaned, not hidden.
// ═══════════════════════════════════════════════════════════════════════════

// Minimum USD value required for a symbol-only match to be protected
export const MIN_SYMBOL_MATCH_USD = 0.50;

export const MANUAL_SAFE_TOKENS = [
  // Stablecoins
  'USDC', 'USDT', 'FDUSD', 'DAI', 'USDY', 'AUSD',

  // Native & Wrapped
  'SUI', 'WETH', 'WBTC', 'WBNB', 'WSOL',

  // Major DeFi tokens
  'CETUS', 'DEEP', 'DEEPBOOK', 'TURBOS', 'SCA', 'SCALLOP',
  'NAVX', 'NAVI', 'BUCK', 'ALPHAFI', 'ALPHA', 'HASUI',
  'VSUI', 'AFSUI', 'SSUI', 'STSUI', 'TYPUS',

  // DEX & Trading
  'KRIYA', 'AFTERMATH', 'AFTERMATH_AF', 'BLUEMOVE', 'BLUEFIN', 'FLOW',

  // Lending
  'SUILEND', 'LENDING',

  // Liquid Staking
  'afSUI', 'vSUI', 'haSUI', 'sSUI', 'stSUI',

  // Infrastructure & Utilities
  'NS', 'SUIA', 'MOVE', 'SOURCE',

  // Oracle & Infrastructure
  'PYTH', 'SWITCHBOARD', 'SUPRA',

  // Walrus ecosystem
  'WAL', 'WALRUS',

  // Meme — Top Tier (>$10M mcap)
  'HIPPO', 'LOFI', 'MIU',

  // Meme — Mid Tier ($1M–$10M mcap)
  'BLUB', 'FUD', 'AAA', 'SUIYAN', 'AXOL', 'TARDI', 'SPAM', 'SUIPEPE',

  // Meme — Emerging (verified)
  'MEMEFI', 'PUGWIF', 'SEND', 'SCALE',

  // Gaming & NFT
  'WOULD', 'KITE',
];

// ═══════════════════════════════════════════════════════════════════════════
// VERIFIED TOKENS CACHE (Tier 1 — Suiet API)
// Matched by full coin_type address — no symbol fallback
// ═══════════════════════════════════════════════════════════════════════════

export const VERIFIED_TOKENS_CACHE = {
  data: null,
  lastUpdated: null,

  async fetch() {
    for (const url of TOKEN_LIST_URLS) {
      try {
        const response = await fetch(url);
        if (!response.ok) continue;

        const data = await response.json();
        if (!Array.isArray(data)) continue;

        this.data = data;
        this.lastUpdated = Date.now();

        localStorage.setItem('verified_tokens', JSON.stringify(data));
        localStorage.setItem('verified_tokens_timestamp', this.lastUpdated.toString());

        return data;
      } catch {
        continue;
      }
    }

    // All endpoints failed — use cache or return null
    const cached = this.loadFromCache();
    if (cached && cached.length > 0) return cached;
    return null;
  },

  loadFromCache() {
    try {
      const cached = localStorage.getItem('verified_tokens');
      const timestamp = localStorage.getItem('verified_tokens_timestamp');
      if (cached && timestamp) {
        this.data = JSON.parse(cached);
        this.lastUpdated = parseInt(timestamp);
        return this.data;
      }
      return null;
    } catch {
      return null;
    }
  },

  async get() {
    const needsRefresh = !this.data ||
      !this.lastUpdated ||
      (Date.now() - this.lastUpdated > CACHE_DURATION);

    if (needsRefresh) return await this.fetch();
    return this.data;
  },

  /**
   * Tier 1 check — ONLY matches by coin_type address, never by symbol.
   * Prevents fake tokens from passing verification just by sharing a name.
   */
  isSafeToken(coinType) {
    if (!this.data || !Array.isArray(this.data)) return false;

    return this.data.some(token => {
      // Address match only — strip trailing whitespace/slashes just in case
      if (token.coin_type) {
        const verified = token.coin_type.trim();
        const input = coinType.trim();
        // Exact match or input starts with verified address
        return input === verified || input.startsWith(verified);
      }
      return false;
    });
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// TIER 2 — PARTNER TOKEN CHECK
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Check if a coin type is a partner token.
 * Matches by full coin type if known, or by symbol as fallback until
 * the real address is confirmed.
 */
export function isPartnerToken(coinType) {
  if (!coinType) return false;

  // Full coin type match (when real addresses are confirmed)
  if (PARTNER_TOKEN_TYPES.some(pt => {
    const clean = pt.trim();
    // Skip placeholder entries
    if (clean.startsWith('0x...')) return false;
    return coinType.trim() === clean || coinType.trim().startsWith(clean);
  })) {
    return true;
  }

  // Symbol fallback — until real TOILET address is confirmed (C1)
  const upper = coinType.toUpperCase();
  return PARTNER_TOKEN_SYMBOLS.some(sym => {
    const pattern = new RegExp(`(^|::)${sym.toUpperCase()}($|::)`, 'i');
    return pattern.test(upper);
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// TIER 3 — SYMBOL MATCH (requires USD value gate in dustDetection.js)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Check if object type matches a known safe token symbol.
 * Returns true for match, but callers should also check USD value
 * before protecting — use isSafeObjectWithValue() for that.
 */
export function isSafeTokenManual(objectType) {
  if (!objectType) return false;

  return MANUAL_SAFE_TOKENS.some(token => {
    const pattern = new RegExp(`(^|::)${token.toUpperCase()}($|::)`, 'i');
    return pattern.test(objectType);
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN SAFE CHECK — THREE-TIER RESULT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Result shape:
 * {
 *   isSafe: boolean,
 *   tier: 'verified' | 'partner' | 'symbol' | null,
 *   requiresValueGate: boolean  — true for symbol matches only
 * }
 */
export function getSafeTokenResult(objectType) {
  if (!objectType) return { isSafe: false, tier: null, requiresValueGate: false };

  // Tier 1 — Verified address
  if (VERIFIED_TOKENS_CACHE.isSafeToken(objectType)) {
    return { isSafe: true, tier: 'verified', requiresValueGate: false };
  }

  // Tier 2 — Partner token
  if (isPartnerToken(objectType)) {
    return { isSafe: true, tier: 'partner', requiresValueGate: false };
  }

  // Tier 3 — Symbol match (requires USD value gate)
  if (isSafeTokenManual(objectType)) {
    return { isSafe: true, tier: 'symbol', requiresValueGate: true };
  }

  return { isSafe: false, tier: null, requiresValueGate: false };
}

/**
 * Legacy-compatible single boolean check.
 * For symbol matches, caller must also verify USD value separately.
 * Use getSafeTokenResult() when you need tier info.
 */
export function isSafeObject(objectType) {
  const result = getSafeTokenResult(objectType);
  return result.isSafe;
}

// ═══════════════════════════════════════════════════════════════════════════
// INITIALIZATION
// ═══════════════════════════════════════════════════════════════════════════

export async function initSafeTokens() {
  const cached = VERIFIED_TOKENS_CACHE.loadFromCache();

  if (!cached || cached.length === 0) {
    // No cache — fetch now (blocking, needed before first scan)
    await VERIFIED_TOKENS_CACHE.fetch().catch(() => null);
  } else {
    // Have cache — refresh in background
    VERIFIED_TOKENS_CACHE.fetch().catch(() => null);
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// UTILITIES
// ═══════════════════════════════════════════════════════════════════════════

export function getSafeTokenStats() {
  return {
    verifiedCount: VERIFIED_TOKENS_CACHE.data ? VERIFIED_TOKENS_CACHE.data.length : 0,
    manualCount: MANUAL_SAFE_TOKENS.length,
    partnerCount: PARTNER_TOKEN_SYMBOLS.length,
    lastUpdated: VERIFIED_TOKENS_CACHE.lastUpdated,
    cacheAge: VERIFIED_TOKENS_CACHE.lastUpdated
      ? Date.now() - VERIFIED_TOKENS_CACHE.lastUpdated
      : null,
  };
}

export async function refreshSafeTokens() {
  return await VERIFIED_TOKENS_CACHE.fetch();
}

export function clearSafeTokenCache() {
  VERIFIED_TOKENS_CACHE.data = null;
  VERIFIED_TOKENS_CACHE.lastUpdated = null;
  localStorage.removeItem('verified_tokens');
  localStorage.removeItem('verified_tokens_timestamp');
}

export default {
  VERIFIED_TOKENS_CACHE,
  MANUAL_SAFE_TOKENS,
  PARTNER_TOKEN_TYPES,
  PARTNER_TOKEN_SYMBOLS,
  MIN_SYMBOL_MATCH_USD,
  initSafeTokens,
  isSafeObject,
  isSafeTokenManual,
  isPartnerToken,
  getSafeTokenResult,
  getSafeTokenStats,
  refreshSafeTokens,
  clearSafeTokenCache,
};
